/**
 * main-readout.ts — live-value resolver for the /main Summary readout tab.
 *
 * Turns the {{token}} placeholders authored in main-readout-content.ts into
 * formatted live values from the /api/main/scorecard payload. See that file's
 * header for the token grammar. Every path is defensive: any missing/null slice
 * resolves to an em dash so the readout never fabricates a number (the same
 * discipline the tiles use).
 *
 * CRITICAL — the readout must read IDENTICALLY to the tiles, so each token kind
 * mirrors the EXACT formatter its tile uses (verified against MainTrendTile,
 * MainBucketedTile, MainPacingTile):
 *   - MAIN-12 headline uses formatCompactCurrencyK ("$82k")  → kind `qtrUsd`
 *   - MAIN-20 headline uses `${v.toFixed(1)} days`           → kind `days`
 *   - generic bucketed headline uses formatTotal (≥1M ? $ : int) → kind `total`
 *   - count activity headlines use formatCountInt            → kind `count`
 *   - MAIN-04 WoW/MoM pills show the ABSOLUTE count change, not a %  → wow/mom
 *   - MAIN-24 "% of target" uses formatPercentToTarget (toFixed(0)) → pctTarget
 *   - MAIN-17 FY figures mirror MainPacingTile (net of Closed-Won) → fy* kinds
 */

import { money, monthLabel, pct as pctFmt, rate } from './exec/fmt.js';
import { FY2027_PIPELINE_TARGET } from './exec/links.js';
import { bucketSeries, todayIso } from './exec/periods.js';
import { describePipelineChange, openTotal, pipelineChange, trendPoints } from './exec/pipeline-trend.js';
import { rilletFrom } from './exec/rillet-source.js';
import { formatCompactCurrencyK, formatCountInt, formatCurrency } from './format.js';
import { computeSnapDelta } from './snap-delta.js';

import type {
  ExecMonthlyStack,
  ExecResponse,
  ExecRilletPanel,
  MainMetricResponse,
  MainScorecardResponse,
} from '@saledash/shared';

const DASH = '—';

type Unit = MainMetricResponse['unit'];

/** Unit-aware single-value formatter — mirrors MainTrendTile.formatMainValue. */
function formatByUnit(value: number, unit: Unit): string {
  switch (unit) {
    case 'USD':
      return formatCurrency(value);
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    case 'days':
      return `${value.toFixed(1)} days`;
    case 'hours':
      return `${value.toFixed(1)} hours`;
    case 'ratio':
      return value.toFixed(2);
    default:
      return formatCountInt(value);
  }
}

/**
 * Generic bucketed-tile headline formatter — mirrors MainBucketedTile.formatTotal
 * ($ at ≥ $1M, thousands-separated integer below). Used by MAIN-32 etc.
 */
function formatTotalLikeTile(value: number): string {
  if (value >= 1_000_000) return formatCurrency(value);
  return formatCountInt(value);
}

/**
 * WoW/MoM change EXACTLY as MainBucketedTile.ActivityDeltaPill renders it — the
 * ABSOLUTE count difference ("up 120" / "down 80" / "flat"), NOT a percentage,
 * so the readout can't disagree with the tile's pill.
 */
function formatCountDelta(pair: { current: number; prior: number } | undefined): string {
  if (!pair) return DASH;
  const diff = pair.current - pair.prior;
  if (diff === 0) return 'flat';
  return `${diff > 0 ? 'up' : 'down'} ${formatCountInt(Math.abs(diff))}`;
}

/** "% of target" mirroring fiscal.formatPercentToTarget (toFixed(0)); null-safe. */
function formatPctOfTarget(value: number | null, target: number | null): string {
  if (value == null || target == null || target === 0) return DASH;
  return `${((value / target) * 100).toFixed(0)}%`;
}

/**
 * MAIN-17 pacing summary — mirrors MainPacingTile's derivation so the readout
 * shows the SAME headline actual + coverage target the tile leads with.
 *
 * The FY numbers come from the MAIN-48 pacing series buckets ({ cumulative,
 * fyTarget, adjustedTarget, closedWonFy }); the coverage target is net of
 * Closed-Won when the series carries `adjustedTarget` (falling back to the gross
 * fyTarget, then metrics['MAIN-17'].target_value). Coverage % uses Math.round to
 * match MainPacingTile's own rounding.
 */
export interface Main17Pacing {
  actual: number | null;
  coverageTarget: number | null;
  grossTarget: number | null;
  closedWonFy: number;
  netOfClosedWon: boolean;
  pct: number | null;
}

function num(b: Record<string, unknown>, key: string): number {
  const v = b[key];
  return typeof v === 'number' ? v : 0;
}

export function computeMain17Pacing(scorecard: MainScorecardResponse): Main17Pacing {
  const series = scorecard.grouped['MAIN-48'];
  const metric = scorecard.metrics['MAIN-17'];
  const buckets = series?.buckets ?? [];

  let grossFromSeries = 0;
  let adjusted = 0;
  let closedWonFy = 0;
  let lastCumulative = 0;
  for (const b of buckets) {
    if (typeof b.label !== 'string' || b.label === '') continue;
    if (grossFromSeries === 0 && num(b, 'fyTarget') > 0) grossFromSeries = num(b, 'fyTarget');
    if (adjusted === 0 && num(b, 'adjustedTarget') > 0) adjusted = num(b, 'adjustedTarget');
    if (closedWonFy === 0 && num(b, 'closedWonFy') > 0) closedWonFy = num(b, 'closedWonFy');
    lastCumulative = num(b, 'cumulative');
  }

  const grossTarget = grossFromSeries > 0 ? grossFromSeries : (metric?.target_value ?? null);
  const netOfClosedWon = adjusted > 0;
  const coverageTarget = netOfClosedWon ? adjusted : grossTarget;
  const actual = series?.total_value ?? (lastCumulative || (metric?.value ?? null));
  const pct =
    coverageTarget != null && coverageTarget > 0 && actual != null
      ? Math.round((actual / coverageTarget) * 100)
      : null;

  return { actual, coverageTarget, grossTarget, closedWonFy, netOfClosedWon, pct };
}

const PACING_KINDS = new Set([
  'fyActual',
  'fyTarget',
  'fyGrossTarget',
  'fyCoverage',
  'fyClosedWon',
  'fyPaceWord',
]);

/**
 * Resolve one `ID|kind` token spec against the live scorecard.
 * `kind` defaults to `value`. Unknown data → em dash.
 */
export function resolveToken(
  scorecard: MainScorecardResponse | undefined,
  spec: string,
  exec?: ExecResponse,
  today: string = todayIso(),
): string {
  if (spec.startsWith('RILLET|')) return resolveRilletToken(spec.slice(7), rilletFrom(exec), today);
  if (spec.startsWith('EXEC|')) return resolveExecToken(exec, spec.slice(5), today);
  if (!scorecard) return DASH;
  const parts = spec.split('|');
  const id = parts[0] ?? '';
  const kind = parts[1] ?? 'value';

  // MAIN-17 FY pacing kinds are derived from the MAIN-48 series (id-independent).
  if (PACING_KINDS.has(kind)) {
    const p = computeMain17Pacing(scorecard);
    switch (kind) {
      case 'fyActual':
        return p.actual != null ? formatCurrency(p.actual) : DASH;
      case 'fyTarget':
        return p.coverageTarget != null ? formatCurrency(p.coverageTarget) : DASH;
      case 'fyGrossTarget':
        return p.grossTarget != null ? formatCurrency(p.grossTarget) : DASH;
      case 'fyCoverage':
        return p.pct != null ? `${String(p.pct)}%` : DASH;
      case 'fyClosedWon':
        return p.closedWonFy > 0 ? formatCurrency(p.closedWonFy) : DASH;
      case 'fyPaceWord':
        // Coverage-based (not time-prorated): the coverage target is a full-FY
        // figure, so this reads "above/below the coverage target", never "pace".
        return p.pct != null
          ? (p.pct >= 100 ? 'above the coverage target' : 'below the coverage target')
          : DASH;
      default:
        return DASH;
    }
  }

  const metric = scorecard.metrics[id];
  const grouped = scorecard.grouped[id];

  switch (kind) {
    case 'value':
      return metric?.value != null ? formatByUnit(metric.value, metric.unit) : DASH;
    case 'target':
      return metric?.target_value != null
        ? formatByUnit(metric.target_value, metric.unit)
        : DASH;
    case 'pctTarget':
      return formatPctOfTarget(metric?.value ?? null, metric?.target_value ?? null);
    case 'count':
      return grouped?.total_value != null ? formatCountInt(grouped.total_value) : DASH;
    case 'total':
      // Generic bucketed headline (MAIN-32 etc.) — mirrors MainBucketedTile.formatTotal.
      return grouped?.total_value != null ? formatTotalLikeTile(grouped.total_value) : DASH;
    case 'qtrUsd':
      // Unit-quarter currency headline (MAIN-12) — mirrors formatCompactCurrencyK.
      return grouped?.total_value != null ? formatCompactCurrencyK(grouped.total_value) : DASH;
    case 'days':
      return grouped?.total_value != null
        ? `${grouped.total_value.toFixed(1)} days`
        : DASH;
    case 'groupedPctTarget':
      return formatPctOfTarget(grouped?.total_value ?? null, grouped?.target_value ?? null);
    case 'wow':
      return formatCountDelta(grouped?.deltas?.wow);
    case 'mom':
      return formatCountDelta(grouped?.deltas?.mom);
    case 'trend': {
      // Direction from real snapshot history (snap-backed metrics only). The
      // percentage mirrors MainTrendTile's WoW chip (round(abs(ratio)*100)).
      const delta = computeSnapDelta(scorecard.snap[id]?.snapshots ?? []);
      if (!delta) return DASH;
      if (delta.flat) return 'flat on the week';
      const pct = Math.round(Math.abs(delta.ratio) * 100);
      return `${delta.up ? 'up' : 'down'} ${String(pct)}% on the week`;
    }
    default:
      return DASH;
  }
}

const TOKEN_RE = /\{\{([^}]+)\}\}/g;

/** Replace every {{token}} in `text` with its resolved live value. */
export function resolveText(
  scorecard: MainScorecardResponse | undefined,
  text: string,
  exec?: ExecResponse,
  today: string = todayIso(),
): string {
  return text.replace(TOKEN_RE, (_match, spec: string) => resolveToken(scorecard, spec.trim(), exec, today));
}

/*
 * quick-261104-ia3 — Rillet tokens (live `rillet` panel, else the dated snapshot).
 *
 * Atomic values:  arr · customers · arrYoY · churn12 · churn12Pct · lastChurn ·
 *   lastChurnMonth · nrr · grr · expansion12 · largestChurn · largestChurnShare ·
 *   top5Share · top1Share · renewalsOpen90
 * Phrases (read correctly whichever way the data moves; neutral fallback when
 * data is missing):  arrYoYAmt · arrMonthsUp · churnShape · provisionalNote ·
 *   nrrRead · concentrationRead
 */

function sharePct(part: number, whole: number): string {
  return whole > 0 ? pctFmt(part / whole) : DASH;
}

function plural(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** Largest single customer that left in the last 12 months (by ARR). */
function largestLoss(R: ExecRilletPanel): { name: string; arr: number } | null {
  let best: { name: string; arr: number } | null = null;
  for (const m of R.months.slice(-12)) {
    for (const c of m.churned) if (!best || c.arr > best.arr) best = c;
  }
  return best;
}

export function resolveRilletToken(kind: string, R: ExecRilletPanel, today: string = todayIso()): string {
  const last = R.months[R.months.length - 1];
  const yearAgo = R.months[R.months.length - 13];
  const months = R.months.slice(-12);
  const churn = months.reduce((s, m) => s + m.churnArr + m.contractionArr, 0);
  const exp = months.reduce((s, m) => s + m.expansionArr, 0);
  const start = yearAgo?.arr ?? 0;
  const top = [...R.topCustomers].sort((a, b) => b.arr - a.arr);
  const lastArr = last?.arr ?? 0;
  const top5 = top.slice(0, 5).reduce((s, c) => s + c.arr, 0);
  switch (kind) {
    case 'arr':
      return last ? money(last.arr) : DASH;
    case 'customers':
      return last ? formatCountInt(last.customers) : DASH;
    case 'arrYoY':
      return last && start ? `${last.arr >= start ? 'up' : 'down'} ${pctFmt(Math.abs(last.arr - start) / start)}` : DASH;
    case 'arrYoYAmt': {
      if (!last || !start) return DASH;
      const d = last.arr - start;
      if (Math.abs(d) < 1) return 'no net change';
      return `a ${money(Math.abs(d))} ${d > 0 ? 'gain' : 'decline'}`;
    }
    case 'arrMonthsUp': {
      const span = R.months.slice(-13);
      const n = span.length - 1;
      if (n < 2) return 'has too little monthly history to call';
      let up = 0;
      for (let i = 1; i < span.length; i++) {
        const a = span[i - 1];
        const b = span[i];
        if (a && b && b.arr > a.arr) up++;
      }
      return `rose in ${String(up)} of the last ${String(n)} months`;
    }
    case 'churn12':
      return months.length ? money(churn) : DASH;
    case 'churn12Pct':
      return start ? pctFmt(churn / start) : DASH;
    case 'expansion12':
      return months.length ? money(exp) : DASH;
    case 'lastChurn':
      return last ? money(last.churnArr) : DASH;
    case 'lastChurnMonth':
      return last ? monthLabel(last.month) : DASH;
    case 'provisionalNote':
      return last?.month === R.provisionalMonth
        ? '; that month is still provisional, because renewals not yet booked in Rillet show as losses until they are'
        : '';
    case 'largestChurn': {
      const b = largestLoss(R);
      return b ? `${b.name} at ${money(b.arr)}` : DASH;
    }
    case 'largestChurnShare': {
      const b = largestLoss(R);
      return b ? sharePct(b.arr, churn) : DASH;
    }
    case 'churnShape': {
      const b = largestLoss(R);
      if (!b || churn <= 0) return 'not tied to any single customer leaving';
      const share = b.arr / churn;
      const lead = share >= 0.25 ? 'concentrated' : 'spread out';
      return `${lead}: the largest single loss, ${b.name} at ${money(b.arr)}, was ${share >= 0.25 ? '' : 'only '}${pctFmt(share)} of the year's total`;
    }
    case 'nrr':
      return start ? pctFmt((start - churn + exp) / start) : DASH;
    case 'grr':
      return start ? pctFmt((start - churn) / start) : DASH;
    case 'nrrRead':
      if (!start) return 'there is not yet a full year of history to judge it';
      return exp >= churn
        ? 'growth from existing customers more than covered what we lost'
        : 'growth from existing customers did not fully cover what we lost, so new customers have to close the gap';
    case 'top5Share':
      return top.length && lastArr ? sharePct(top5, lastArr) : DASH;
    case 'top1Share':
      return top[0] && lastArr ? sharePct(top[0].arr, lastArr) : DASH;
    case 'concentrationRead': {
      if (!top.length || !lastArr) return 'customer concentration is not available yet';
      const share = top5 / lastArr;
      return share >= 0.3
        ? `the five largest customers hold ${pctFmt(share)} of ARR, so one renewal among them moves retention more than many smaller ones combined`
        : `the five largest customers hold ${pctFmt(share)} of ARR, so no single renewal decides the year`;
    }
    case 'renewalsOpen90': {
      const end = addDaysIso(today, 90);
      const open = R.renewals.filter((r) => !r.renewed && r.endDate >= today && r.endDate <= end);
      if (open.length === 0) return 'none';
      return `${plural(open.length, 'contract', 'contracts')} (${money(open.reduce((s, r) => s + r.value, 0))})`;
    }
    default:
      return DASH;
  }
}

/*
 * quick-261104-ia3 — exec-panel tokens (GET /api/main/exec):
 *
 * Atomic values:  pipelineOpen · pipelineWithPotential · potentialCount ·
 *   potentialValue · potentialPctOfOpen · nextYearPipe · nextYearPct ·
 *   nextYearTarget · nlWin · expWin
 * Phrases:  pipeChange12 · topStage · meetingsTrend · nlWinVsLastYear ·
 *   dealSizeMix · cycleGap · mktTopChannel · mktPipeTrend · mktVolumeVsQuality
 */

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** "up 12% on" / "down 8% on" / "level with" — relative change wording. */
function relWord(now: number, prior: number): string {
  const r = (now - prior) / Math.abs(prior);
  if (Math.abs(r) < 0.005) return 'level with';
  return `${r > 0 ? 'up' : 'down'} ${String(Math.round(Math.abs(r) * 100))}% on`;
}

function thresholdLabel(usd: number): string {
  return usd >= 1_000_000_000 ? `$${String(Math.round(usd / 100_000_000) / 10)}B` : `$${String(Math.round(usd / 1_000_000))}M`;
}

function weightedAvg(rows: { v: number | null; n: number }[]): number | null {
  let s = 0;
  let n = 0;
  for (const r of rows) {
    if (r.v != null && r.n > 0) {
      s += r.v * r.n;
      n += r.n;
    }
  }
  return n > 0 ? s / n : null;
}

/** Channel totals over a monthly stack, largest first, 'Other' last. */
function channelTotals(stack: ExecMonthlyStack): { name: string; total: number }[] {
  return stack.series
    .map((s) => ({ name: s.name, total: s.values.reduce((a, v) => a + v, 0) }))
    .sort((a, b) => (a.name === 'Other' ? 1 : b.name === 'Other' ? -1 : b.total - a.total));
}

export function resolveExecToken(exec: ExecResponse | undefined, kind: string, today: string = todayIso()): string {
  if (!exec) return DASH;
  const funnel = exec.panels.funnel?.payload;
  const wr = exec.panels.winrate?.payload;
  const cov = exec.panels.coverage?.payload;
  const cycle = exec.panels.cycle?.payload;
  const act = exec.panels.activity?.payload;
  const mkt = exec.panels.marketing?.payload;
  const avg = exec.avgDealSize;
  const nextYear = Number(today.slice(0, 4)) + 1;
  const nextYearPipe = cov ? cov.cells.filter((c) => c.year === nextYear).reduce((s, c) => s + c.open, 0) : null;
  const open = funnel ? openTotal(funnel.current) : 0;
  const potential = funnel && avg != null ? funnel.potential.length * avg : null;
  switch (kind) {
    case 'pipelineOpen':
      return funnel ? money(open) : DASH;
    case 'pipelineWithPotential':
      return funnel ? money(open + (potential ?? 0)) : DASH;
    case 'potentialCount':
      return funnel ? String(funnel.potential.length) : DASH;
    case 'potentialValue':
      return potential != null ? money(potential) : DASH;
    case 'potentialPctOfOpen':
      return potential != null && open > 0 ? pctFmt(potential / open) : DASH;
    case 'pipeChange12': {
      if (!funnel) return 'not yet comparable with a year ago';
      const c = pipelineChange(trendPoints(funnel, today), avg);
      return c ? describePipelineChange(c) : 'not yet comparable with a year ago';
    }
    case 'topStage': {
      if (!funnel || open <= 0) return 'not available yet';
      const [stage, amt] = Object.entries(funnel.current.byStage).sort((a, b) => b[1] - a[1])[0] ?? ['', 0];
      return stage ? `${stage}, with ${pctFmt(amt / open)} of open dollars` : 'not available yet';
    }
    case 'meetingsTrend': {
      if (!act) return 'there is not enough weekly history yet';
      const w = bucketSeries(act.meetingsBooked, 'week', today, act.since, 9).filter((b) => !b.partial);
      if (w.length < 8) return 'there is not enough weekly history yet';
      const recent = w.slice(-4).reduce((s, b) => s + b.c, 0);
      const prior = w.slice(-8, -4).reduce((s, b) => s + b.c, 0);
      const head = `${formatCountInt(recent)} in the last four complete weeks`;
      if (prior === 0) return `${head}, against none in the four weeks before`;
      return `${head}, ${relWord(recent, prior)} the ${formatCountInt(prior)} booked in the four weeks before`;
    }
    case 'nextYearPipe':
      return nextYearPipe != null ? money(nextYearPipe) : DASH;
    case 'nextYearPct':
      return nextYearPipe != null ? pctFmt(nextYearPipe / FY2027_PIPELINE_TARGET) : DASH;
    case 'nextYearTarget':
      return money(FY2027_PIPELINE_TARGET);
    case 'nlWin':
      return wr ? pctFmt(rate(wr.ytd.newWon, wr.ytd.newLost)) : DASH;
    case 'expWin':
      return wr ? pctFmt(rate(wr.ytd.expWon, wr.ytd.expLost)) : DASH;
    case 'nlWinVsLastYear': {
      const none = 'cannot be compared with last year yet';
      if (!wr || wr.quarters.length === 0) return none;
      const fy = Math.max(...wr.quarters.map((q) => q.fy));
      const fq = Math.max(...wr.quarters.filter((q) => q.fy === fy).map((q) => q.fq));
      const prior = wr.quarters.filter((q) => q.fy === fy - 1 && q.fq <= fq);
      const now = rate(wr.ytd.newWon, wr.ytd.newLost);
      const then = prior.length
        ? rate(
            prior.reduce((s, q) => s + q.newWon, 0),
            prior.reduce((s, q) => s + q.newLost, 0),
          )
        : null;
      if (now == null || then == null) return none;
      const pts = Math.round((now - then) * 100);
      if (pts === 0) return `is level with this point last year (${pctFmt(then)})`;
      return `is ${pts > 0 ? 'up' : 'down'} from ${pctFmt(then)} at this point last year`;
    }
    case 'dealSizeMix': {
      const none = 'average deal size by type is not available yet';
      if (!wr) return none;
      const q4 = wr.quarters.slice(-4);
      if (!q4.some((q) => q.newWonAmt != null || q.expWonAmt != null)) return none;
      const nW = q4.reduce((s, q) => s + q.newWon, 0);
      const eW = q4.reduce((s, q) => s + q.expWon, 0);
      const nA = q4.reduce((s, q) => s + (q.newWonAmt ?? 0), 0);
      const eA = q4.reduce((s, q) => s + (q.expWonAmt ?? 0), 0);
      if (nW === 0 || eW === 0) return none;
      const nAvg = nA / nW;
      const eAvg = eA / eW;
      const rel =
        Math.abs(eAvg - nAvg) / nAvg < 0.05
          ? 'about the same'
          : eAvg > nAvg
            ? `about ${(eAvg / nAvg).toFixed(1)}× the new-logo average`
            : `about ${pctFmt(eAvg / nAvg)} of the new-logo average`;
      return `expansion deals won over the last four quarters averaged ${money(eAvg)} against ${money(nAvg)} for new logos, ${rel}`;
    }
    case 'cycleGap': {
      const none = 'there are too few won deals by company size to compare yet';
      if (!cycle) return none;
      const q4 = cycle.quarters.slice(-4);
      const large = weightedAvg(q4.map((q) => ({ v: q.large, n: q.largeN })));
      const small = weightedAvg(q4.map((q) => ({ v: q.small, n: q.smallN })));
      if (large == null || small == null || small <= 0) return none;
      const thr = thresholdLabel(cycle.thresholdUsd);
      const head = `won deals at companies with ${thr}+ in revenue took ${formatCountInt(large)} days on average over the last four quarters, against ${formatCountInt(small)} for smaller companies`;
      const r = (large - small) / small;
      if (Math.abs(r) < 0.05) return `${head}, about the same`;
      return `${head}, about ${String(Math.round(Math.abs(r) * 100))}% ${r > 0 ? 'longer' : 'shorter'}`;
    }
    case 'mktTopChannel': {
      if (!mkt) return 'not available yet';
      const t = channelTotals(mkt.pipelineByChannel);
      const grand = t.reduce((s, c) => s + c.total, 0);
      const best = t[0];
      if (!best || grand <= 0) return 'not available yet';
      return `${best.name}, with ${pctFmt(best.total / grand)} of the marketing-sourced pipeline created`;
    }
    case 'mktPipeTrend': {
      const none = 'not available yet';
      if (!mkt) return none;
      const { months: ms, series } = mkt.pipelineByChannel;
      const totals = ms.map((_, i) => series.reduce((s, x) => s + (x.values[i] ?? 0), 0));
      const full = ms[ms.length - 1] === today.slice(0, 7) ? totals.slice(0, -1) : totals;
      if (full.length < 6) return none;
      const recent = full.slice(-3).reduce((a, v) => a + v, 0);
      const prior = full.slice(-6, -3).reduce((a, v) => a + v, 0);
      const head = `${money(recent)} over the last three full months`;
      if (prior === 0) return `${head}, against none in the three before`;
      return `${head}, ${relWord(recent, prior)} the ${money(prior)} in the three before`;
    }
    case 'mktVolumeVsQuality': {
      const none = 'conversion by channel is not available yet';
      if (!mkt) return none;
      const vol = channelTotals(mkt.pipelineByChannel).find((c) => c.name !== 'Other');
      const lines = mkt.leadToOppByChannel;
      const qi = lines.quarters.length >= 2 ? lines.quarters.length - 2 : lines.quarters.length - 1;
      const qLabel = lines.quarters[qi];
      if (!vol || qLabel == null) return none;
      let best: { name: string; v: number } | null = null;
      for (const s of lines.series) {
        const v = s.values[qi];
        if (s.name === 'Other' || v == null || (s.n[qi] ?? 0) < 10) continue;
        if (!best || v > best.v) best = { name: s.name, v };
      }
      if (!best) return none;
      if (best.name === vol.name) {
        return `the channel with the most pipeline, ${vol.name}, also converts best, turning ${pctFmt(best.v)} of its leads into opportunities in ${qLabel}`;
      }
      return `${vol.name} brings the most pipeline, but ${best.name} converts best, turning ${pctFmt(best.v)} of its leads into opportunities in ${qLabel}`;
    }
    default:
      return DASH;
  }
}
